
import React, { useEffect, useState } from 'react';
import {
  IonButton,
  IonCard,
  IonContent,
  IonFooter,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonToolbar
} from '@ionic/react';
import { getFileData } from '../utilities/ApiTasks';
import { 
  getFile, 
  getFileBase64, 
  storeFile, 
  getJob, 
  getJobNotes, 
  getSubmittedNotes, 
  getDocumentBase64,
} from '../utilities/Storage';
import { getMime } from '../utilities/Mime';
import nl2br from 'react-nl2br';
import moment from 'moment';
import { FileOpener } from '@ionic-native/file-opener';
import { Filesystem, FilesystemDirectory } from '@capacitor/filesystem';
import {Toolbar} from "./Toolbar";

export default function JobNotes({history, match}) {

  const { id } = match.params;

  const [job, setJob] = useState();
  const [existingNotes, setExistingNotes] = useState();
  const [newNotes, setNewNotes] = useState();

  useEffect(() => {

    const getNotes = async () => {
      const j = await getJob(id);
      setJob(j);

      const en = await getJobNotes(id);
      en.sort((a, b) => (a.note.created > b.note.created) ? 1 : -1);
      setExistingNotes(en);

      const nn = await getSubmittedNotes(id);
      setNewNotes(nn);
    };

    getNotes();

    history.listen(val => {
      if (val.pathname === `/job/${id}/notes`) {
        getNotes();
      }
    });

  }, []);

  if (!job || !existingNotes || !newNotes) {
    return null;
  }

  const pageTitle = `${id} notes`;

  return (
    <IonPage>
      <IonHeader>
        <Toolbar title={pageTitle} backButtonHref="/" />
      </IonHeader>
      <IonContent>
        <IonList lines="full">
          {existingNotes.length === 0 && newNotes.length === 0 &&
          <IonItem>
            <IonLabel>No notes found.</IonLabel>
          </IonItem>
          }
          {existingNotes.map((note, i) => 
          <ExistingNote 
            note={note.note} 
            documents={job.documents} 
            key={'note' + i} 
            type={note.workorder ? 'job' : 'booking'} 
          />
          )}
          {newNotes.map((note, i) => 
          <NewNote note={note} key={'newNote' + i} />  
          )}
        </IonList>
      </IonContent>
      <IonFooter no-border>
        <IonToolbar className="ion-text-center">
          <IonButton 
            color="medium" 
            expand="block"
            onClick={() => history.push(`/job/${id}/addnote`) }
          >
            add note
          </IonButton>
        </IonToolbar>
      </IonFooter>       
    </IonPage>
  );
}

const ExistingNote = React.memo(function ExistingNote({note, documents, type}) {
  
  const [files, setFiles] = useState();

  useEffect(() => {

    const getFiles = async () => {

      let filesArr = [];
      let noteText = note.notetexts.map(noteText => {return noteText.notetext}).join("\n");
      const regDocuments = noteText.match(/\[documents:.*?\]/g);
  
      if (regDocuments) {
        for (const matches of regDocuments) {
          for (const id of matches.replace("[documents:", "").replace("]", "").split(",")) {
            let documentAssociation = documents.find(document => {return document.id === parseInt(id, 10)});

            let file = undefined;
            let type = undefined;
            let fileName = undefined;

            if (documentAssociation.image) { 
              file = documentAssociation.image.file; 
              type = 'image';
              fileName = documentAssociation.image.filename;
            }
            if (documentAssociation.document) { 
              file = documentAssociation.document.file; 
              type = 'document';
              fileName = documentAssociation.document.filename;
            }

            if (file) {
              const fileId = file.substr(file.lastIndexOf("/") + 1);

              let blob = await getFile(fileId);

              if (blob === '') {
                // we haven't got the file stored, so get it afresh
                const raw = await getFileData(fileId);
                await storeFile(fileId, raw);
                blob = await getFile(fileId);
              }

              filesArr.push({
                id: documentAssociation.id,
                fileId,
                type,
                blob,
                fileName,
              });
            }
          };          
        };
      }

      setFiles(filesArr);
    }

    getFiles();

  }, []);

  const getDocumentsBlock = () => {

    let docs = [];
    let images = [];

    files.forEach(file => {
      if (file.type === 'document') {
        docs.push(
          <React.Fragment key={file.id}>
            <IonButton size="sm" onClick={() => {downloadDocument(file.fileName, file.fileId)}}>
              {file.fileName}
            </IonButton>
            {' '}
          </React.Fragment>
        );
      } else if (file.type === 'image') {
        const imgSrc = URL.createObjectURL(file.blob);

        images.push(
          <IonCard key={file.id}>
            <img src={imgSrc} alt={file.id} />
            {/* <IonCardHeader>
              <IonCardSubtitle>
                {file.fileName}
              </IonCardSubtitle>
            </IonCardHeader> */}
          </IonCard>                
        );
      }
    });

    const downloadDocument = async (fileName, fileId) => {
      const base64 = await getFileBase64(fileId);

      await Filesystem.writeFile({
        data: base64,
        path: fileName,
        directory: FilesystemDirectory.Application
      });

      const uri = await Filesystem.getUri({
        directory: FilesystemDirectory.Application,
        path: fileName
      });

      const mime = getMime(fileName);

      FileOpener.open(uri.uri, mime);
    }

    return (
      <React.Fragment>
        {images.length > 0 &&
        <div className="m-0 mt-2">
          {images}
        </div>
        }
        {docs.length > 0 &&
        <div className="m-0 mt-2">
          {docs}
        </div>
        }    
      </React.Fragment>
    );
  }

  let noteText = note.notetexts.map(noteText => {return noteText.notetext}).join("\n");
  noteText = noteText.replace(/\[documents:.*?\]/g, "");

  if (!files) {
    return (
      <IonItem>
        <IonLabel className="ion-text-wrap">
          {note.subject}
          <p>{moment(note.created).format('DD/MM/YY HH:mm')} - {type} note</p>
          <p>{nl2br(noteText)}</p>
          Loading documents and notes...
        </IonLabel>
      </IonItem>
    );
  }

  let documentsBlock = getDocumentsBlock(noteText);

  return (
    <IonItem>
      <IonLabel className="ion-text-wrap">
        {note.subject}
        <p>{moment(note.created).format('DD/MM/YY HH:mm')} - {type} note</p>
        <p>{nl2br(noteText)}</p>
        {documentsBlock}
      </IonLabel>
    </IonItem>
  );
});

const NewNote = React.memo(function NewNote({note}) {

  const getDocumentsBlock = () => {

    let docs = [];
    let images = [];

    if (note.documents) {
      note.documents.forEach(document => {
        if (typeof document === 'object') { return; }

        docs.push(
          <React.Fragment key={document}>
            <IonButton size="sm" onClick={() => {downloadDocument(document)}}>
              {document}
            </IonButton>
            {' '}
          </React.Fragment>
        );
      });
    }

    if (note.photos) {
      note.photos.forEach(photo => {
        const fileName = photo.substr(photo.lastIndexOf("/") + 1);

        images.push(
          <IonCard key={photo}>
            <img src={photo} alt={fileName} />
            {/* <IonCardHeader>
              <IonCardSubtitle>
                {fileName}
              </IonCardSubtitle>
            </IonCardHeader> */}
          </IonCard>                
        );
      });
    }

    const downloadDocument = async (fileName) => {
      const base64 = await getDocumentBase64(fileName);

      await Filesystem.writeFile({
        data: base64,
        path: fileName,
        directory: FilesystemDirectory.Application
      });

      const uri = await Filesystem.getUri({
        directory: FilesystemDirectory.Application,
        path: fileName
      });

      const mime = getMime(fileName);

      FileOpener.open(uri.uri, mime);
    }

    return (
      <React.Fragment>
        {images.length > 0 &&
        <div className="m-0 mt-2">
          {images}
        </div>
        }
        {docs.length > 0 &&
        <div className="m-0 mt-2">
          {docs}
        </div>
        }    
      </React.Fragment>
    );
  }

  let documentsBlock = getDocumentsBlock();

  return (
    <IonItem>
      <IonLabel className="ion-text-wrap">
        <p>{note.noteText}</p>
        {documentsBlock}
      </IonLabel>
    </IonItem>
  );
});